import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import { getFullFileUrl } from '../../Helpers/UrlHelpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSave,
    faArrowRight,
    faRobot,
    faImage,
    faInfoCircle,
    faTags,
} from '@fortawesome/free-solid-svg-icons';
import Loader from '../Shared/Loader';
import FileUploader from '../Shared/FileUploader';

const EditAppView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { appView } = location.state || {};

    const [loading, setLoading] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);

    const [formData, setFormData] = useState({
        Version: appView?.version || '',
        Description: appView?.description || '',
        priority: appView?.priority || 'Normal',
        AppIcon: null,
        Sreenshot1: null,
        Sreenshot2: null,
        Sreenshot3: null,
        Sreenshot4: null,
        Sreenshot5: null,
    });

    const [previews, setPreviews] = useState({
        AppIcon: getFullFileUrl(appView?.app_icon),
        Sreenshot1: getFullFileUrl(appView?.sreenshot1),
        Sreenshot2: getFullFileUrl(appView?.sreenshot2),
        Sreenshot3: getFullFileUrl(appView?.sreenshot3),
        Sreenshot4: getFullFileUrl(appView?.sreenshot4),
        Sreenshot5: getFullFileUrl(appView?.sreenshot5),
    });

    useEffect(() => {
        if (!appView) {
            toastHelper.error('لم يتم العثور على بيانات العرض');
            navigate('/admin/dashboard');
        }
    }, [appView, navigate]);

    const handleFileChange = (e, field) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, [field]: file }));
            setPreviews(prev => ({ ...prev, [field]: URL.createObjectURL(file) }));
        }
    };

    const handleAIGenerate = async () => {
        setAiLoading(true);
        try {
            const response = await ApiServices.post(
                'AppAbout/GenerateContent',
                "Please generate a professional update description for version " + formData.Version,
                false,
                80000,
                { responseType: 'text' }
            );
            setFormData(prev => ({ ...prev, Description: response }));
            toastHelper.success('تم تحديث الوصف بنجاح');
        } catch (e) {
            toastHelper.error('فشل في توليد الوصف');
        } finally {
            setAiLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.Version || !formData.Description) {
            return toastHelper.error('يرجى تعبئة كافة الحقول المطلوبة');
        }

        setLoading(true);
        try {
            const data = new FormData();
            data.append('Version', formData.Version);
            data.append('Description', formData.Description);
            data.append('priority', formData.priority);

            // Only append files if they were changed
            if (formData.AppIcon) data.append('AppIcon', formData.AppIcon);
            if (formData.Sreenshot1) data.append('Sreenshot1', formData.Sreenshot1);
            if (formData.Sreenshot2) data.append('Sreenshot2', formData.Sreenshot2);
            if (formData.Sreenshot3) data.append('Sreenshot3', formData.Sreenshot3);
            if (formData.Sreenshot4) data.append('Sreenshot4', formData.Sreenshot4);
            if (formData.Sreenshot5) data.append('Sreenshot5', formData.Sreenshot5);

            const response = await ApiServices.put(`AppView/update/${appView.id}`, data, true);
            if (response && response.check) {
                toastHelper.success('تم تحديث عرض التطبيق بنجاح');
                navigate('/admin/dashboard');
            } else {
                toastHelper.error(response.message || 'فشل في التحديث');
            }
        } catch (error) {
            toastHelper.error('حدث خطأ أثناء الاتصال بالسيرفر');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-appview-page">
            {loading && <Loader text="جاري الحفظ..." />}
            <div className="form-container">
                <header className="form-header">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <FontAwesomeIcon icon={faArrowRight} />
                        <span>عودة</span>
                    </button>
                    <h1>تعديل عرض التطبيق: {appView?.version}</h1>
                </header>

                <form onSubmit={handleSubmit}>
                    <div className="form-grid">
                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faTags} /> نسخة الإصدار (Version)</label>
                            <input
                                type="text"
                                value={formData.Version}
                                onChange={(e) => setFormData({ ...formData, Version: e.target.value })}
                                placeholder="مثلاً: 1.0.5"
                                required
                            />
                        </div>

                        <div className="input-group">
                            <label><FontAwesomeIcon icon={faInfoCircle} /> الأولوية (Priority)</label>
                            <select
                                value={formData.priority}
                                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                            >
                                <option value="Normal">عادي (Normal)</option>
                                <option value="High">مرتفع (High)</option>
                            </select>
                        </div>

                        <div className="input-group full-width">
                            <label className="desc-label">
                                <span><FontAwesomeIcon icon={faInfoCircle} /> وصف الإصدار</span>
                                <button type="button" className="ai-btn" onClick={handleAIGenerate} disabled={aiLoading}>
                                    <FontAwesomeIcon icon={faRobot} spin={aiLoading} />
                                    <span>{aiLoading ? 'جاري التوليد...' : 'توليد بالذكاء الاصطناعي'}</span>
                                </button>
                            </label>
                            <textarea
                                value={formData.Description}
                                onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
                                placeholder="اكتب وصفاً للتغييرات..."
                                rows="4"
                                required
                            />
                        </div>

                        <div className="input-group full-width">
                            <label><FontAwesomeIcon icon={faImage} /> أيقونة التطبيق</label>
                            <div className="icon-uploader-wrap">
                                <FileUploader
                                    id="edit-app-icon-up"
                                    accept="image/*"
                                    MaxFileSize={5}
                                    previewUrl={previews.AppIcon}
                                    onFileChange={(e) => handleFileChange(e, 'AppIcon')}
                                    placeholder="أيقونة التطبيق"
                                    subText="PNG أو JPG حتى 5MB"
                                    buttonText="اختر أيقونة"
                                    changeButtonText="تغيير الأيقونة"
                                />
                            </div>
                        </div>

                        <div className="screenshots-section full-width">
                            <label className="ss-section-label">
                                لقطات الشاشة (Screenshots)
                                <span className="optional-hint">اتركها فارغة للإبقاء على الحالي</span>
                            </label>
                            <div className="ss-grid">
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} className={`ss-upload-item ${i <= 3 ? 'required-ss' : 'optional-ss'}`}>
                                        <span className="ss-num-badge">{i}</span>
                                        <FileUploader
                                            id={`ss-up-edit-${i}`}
                                            accept="image/*"
                                            MaxFileSize={5}
                                            previewUrl={previews[`Sreenshot${i}`]}
                                            onFileChange={(e) => handleFileChange(e, `Sreenshot${i}`)}
                                            placeholder={`لقطة ${i}`}
                                            subText="اختيارية"
                                            buttonText="اختر"
                                            changeButtonText="تغيير"
                                            BackgroundColor={previews[`Sreenshot${i}`] ? '#f0f4ff' : '#f9fafb'}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <footer className="form-footer">
                        <button type="submit" className="submit-btn" disabled={loading}>
                            <FontAwesomeIcon icon={faSave} />
                            <span>تحديث عرض التطبيق</span>
                        </button>
                    </footer>
                </form>
            </div>

            <style>{`
                .add-appview-page { padding: 40px 20px; background: #f8fafc; min-height: 100vh; direction: rtl;   font-family: "Zain", sans-serif; }
                .form-container { max-width: 960px; margin: 0 auto; background: white; padding: 40px; border-radius: 24px; box-shadow: 0 10px 40px rgba(0,0,0,0.05); }
                .form-header { display: flex; align-items: center; gap: 20px; margin-bottom: 40px; border-bottom: 2px solid #f1f5f9; padding-bottom: 20px; }
                .back-btn { background: #f1f5f9; border: none; padding: 10px 20px; border-radius: 12px; cursor: pointer; color: #64748b; font-weight: 700; display: flex; align-items: center; gap: 10px; }
                .form-header h1 { margin: 0; font-size: 1.8rem; color: #1e293b; font-weight: 800; }

                .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; }
                .full-width { grid-column: 1 / -1; }

                .input-group label { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; font-weight: 700; color: #475569; font-size: 0.95rem; }
                .input-group input[type="text"], .input-group select, .input-group textarea {
                    width: 100%; padding: 14px 18px; border: 2px solid #e2e8f0; border-radius: 12px; font-size: 1rem;
                      font-family: "Zain", sans-serif; outline: none; transition: border-color 0.2s; box-sizing: border-box;
                }
                .input-group input:focus, .input-group select:focus, .input-group textarea:focus { border-color: #6366f1; }

                .desc-label { justify-content: space-between; width: 100%; }
                .ai-btn { background: #f0f7ff; color: #6366f1; border: 1px dashed #6366f1; padding: 6px 15px; border-radius: 8px;
                    cursor: pointer; display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 0.85rem;
                    transition: all 0.2s;   font-family: "Zain", sans-serif; }
                .ai-btn:hover { background: #e0f2fe; }

                .icon-uploader-wrap { width: 100%; }

                .ss-section-label { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; font-weight: 700; color: #475569; font-size: 0.95rem; }
                .required-hint { font-size: 0.75rem; background: #fee2e2; color: #ef4444; padding: 3px 10px; border-radius: 20px; font-weight: 700; }
                .optional-hint { font-size: 0.75rem; background: #f1f5f9; color: #64748b; padding: 3px 10px; border-radius: 20px; font-weight: 600; }

                .ss-grid { display: flex; flex-direction: column; gap: 20px; }
                .ss-upload-item { position: relative; padding-top: 12px; }
                .ss-num-badge { display: inline-flex; align-items: center; margin-bottom: 8px; font-size: 0.78rem; font-weight: 800;
                    color: white; border-radius: 20px; padding: 3px 12px; position: absolute; top: 0; right: 0; background: #6366f1; }
                .required-ss .ss-num-badge { background: #ef4444; }
                .optional-ss .ss-num-badge { background: #94a3b8; }

                .form-footer { margin-top: 40px; padding-top: 30px; border-top: 2px solid #f1f5f9; display: flex; justify-content: center; }
                .submit-btn { background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); color: white; border: none; padding: 16px 60px;
                    border-radius: 15px; font-size: 1.1rem; font-weight: 700; cursor: pointer; display: flex; align-items: center;
                    gap: 12px; transition: all 0.3s; box-shadow: 0 10px 20px -5px rgba(99, 102, 241, 0.4);   font-family: "Zain", sans-serif; }
                .submit-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 15px 30px -5px rgba(99, 102, 241, 0.5); }

                @media (max-width: 768px) {
                    .add-appview-page { padding: 20px 10px; }
                    .form-container { padding: 20px; border-radius: 15px; }
                    .form-header { gap: 10px; margin-bottom: 25px; }
                    .form-header h1 { font-size: 1.4rem; }
                    .back-btn { padding: 8px 15px; font-size: 0.9rem; }
                    .form-grid { grid-template-columns: 1fr; gap: 20px; }
                    .desc-label { flex-direction: column; align-items: flex-start; gap: 10px; }
                    .ai-btn { width: 100%; justify-content: center; }
                    .submit-btn { width: 100%; padding: 14px 20px; }
                    .icon-uploader-wrap { width: 100%; }
                }
            `}</style>
        </div>
    );
};

export default EditAppView;
